import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { sv as svLocale } from "date-fns/locale";
import { NewsArticle } from "@/data/newsData";
import { trackEvent } from "@/lib/analytics";
import { useLanguage } from "@/i18n/useLanguage";

interface NewsCardProps {
  article: NewsArticle;
  showExcerpt?: boolean;
  className?: string;
}

const NewsCard = ({ article, showExcerpt = true, className }: NewsCardProps) => {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const categoryLabel = t(`newsPage.categories.${article.category}`, {
    defaultValue: article.category,
  });
  const dateLabel =
    lang === "sv"
      ? format(article.date, "d MMMM yyyy", { locale: svLocale })
      : format(article.date, "MMMM d, yyyy");

  const handleClick = () =>
    trackEvent("news_article_click", {
      article_id: article.id,
      article_title: article.title,
      article_category: article.category,
      outbound_url: article.externalLink,
    });

  return (
    <Card className={cn(
      "group overflow-hidden hover:-translate-y-1 hover:shadow-glow-card hover:border-primary/40 transition-all duration-300 flex flex-col h-full bg-card/80 backdrop-blur-sm",
      className
    )}>
      {/* Image or Gradient Placeholder */}
      <a
        href={article.externalLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        aria-label={article.title}
        className="block overflow-hidden"
      >
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            width="400"
            height="192"
          />
        ) : (
          <div className="h-48 w-full bg-gradient-brand opacity-70 transition-transform duration-500 group-hover:scale-105" />
        )}
      </a>

      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-xs">
            {categoryLabel}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {dateLabel}
          </span>
        </div>
        <CardTitle className="text-xl md:text-2xl font-bold">
          <a
            href={article.externalLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm transition-colors hover:text-primary group-hover:text-primary hover:underline underline-offset-4 decoration-primary/60"
          >
            {article.title}
          </a>
        </CardTitle>
      </CardHeader>

      {showExcerpt && (
        <CardContent className="flex flex-col flex-1">
          <CardDescription className="text-sm md:text-base mb-4">
            {article.excerpt}
          </CardDescription>

          {article.author && (
            <p className="text-xs text-muted-foreground mt-auto">
              {t("newsPage.card.by", { author: article.author })}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default NewsCard;
